import React, { useState, useEffect } from 'react';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { classInfoService } from '../services/ClassInfoService';
import ChildTabSelector from './ChildTabSelector';
import GradeDetailView from './GradeDetailView';
import './ParentGradesView.css';
import './ParentDetailPages.css';

function ParentGradesView({ children }) {
  const [selectedChild, setSelectedChild] = useState(null);
  const [student, setStudent] = useState(null);
  const [classInfo, setClassInfo] = useState(null);
  const [grades, setGrades] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedGrade, setSelectedGrade] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState(null);
  const [showDistribution, setShowDistribution] = useState(false);

  const handleChildSelect = (child) => {
    setSelectedChild(child);
  };

  useEffect(() => {
    if (selectedChild) {
      // iOS uses the child data directly and filters grades by studentId
      // Skip the student document reload - use child data directly
      console.log('🔥 GRADES: Selected child:', selectedChild);
      console.log('🔥 GRADES: child.id:', selectedChild.id);
      console.log('🔥 GRADES: child.studentId:', selectedChild.studentId);
      
      // Use child data directly (iOS approach)
      const studentData = { ...selectedChild };
      if (!studentData.fullName && studentData.firstName) {
        studentData.fullName = `${studentData.firstName} ${studentData.lastName || ''}`.trim();
      }
      setStudent(studentData);
      
      // Load class info
      if (studentData.classId) {
        loadClassInfo(studentData.classId);
      }
      
      // Load grades using the child's UUID id
      loadGradesForStudent(studentData.id, studentData.classId);
    }
  }, [selectedChild]);

  const loadClassInfo = async (classId) => {
    try {
      const classData = await classInfoService.getClassInfo(classId);
      console.log('🔥 GRADES: ✅ Class info from service:', classData);
      setClassInfo(classData);
    } catch (error) {
      console.log('🔥 GRADES: ❌ Error from ClassInfoService:', error);
      setClassInfo({
        id: classId,
        name: 'Class ' + classId.slice(-8),
        grade: 'N/A',
        source: 'error'
      });
    }
  };

  const loadGradesForStudent = async (studentId, classId) => {
    setLoading(true);
    const gradesData = [];
    const assignmentsData = [];
    
    if (!classId) {
      setGrades(gradesData);
      setAssignments(assignmentsData);
      setLoading(false);
      return;
    }

    console.log('🔥 GRADES: Loading grades for student:', studentId, '- classId:', classId);
    
    try {
      const assignmentsSubcollection = collection(db, 'classes', classId, 'assignments');
      const assignmentsSnapshot = await getDocs(assignmentsSubcollection);
      
      console.log('🔥 GRADES: Found', assignmentsSnapshot.docs.length, 'assignments to check for grades');
      
      for (const assignmentDoc of assignmentsSnapshot.docs) {
        const assignmentId = assignmentDoc.id;
        const assignmentData = assignmentDoc.data();
        assignmentsData.push({ id: assignmentId, ...assignmentData });
        
        try {
          const gradesSubcollection = collection(db, 'classes', classId, 'assignments', assignmentId, 'grades');
          
          // Query by studentId (UUID)
          const gradesQuery = query(gradesSubcollection, where('studentId', '==', studentId));
          const gradesSnapshot = await getDocs(gradesQuery);
          
          gradesSnapshot.forEach(doc => {
            const gradeData = doc.data();
            gradesData.push({
              id: doc.id,
              assignmentId: assignmentId,
              ...gradeData
            });
            console.log('🔥 GRADES: Found grade:', gradeData);
          });
        } catch (error) {
          console.log('🔥 GRADES: Error accessing grades for assignment', assignmentId, ':', error);
        }
      }
      
      console.log('🔥 GRADES: Total grades found:', gradesData.length);
      console.log('🔥 GRADES: Total assignments loaded:', assignmentsData.length);
    } catch (error) {
      console.log('🔥 GRADES: Error accessing assignments subcollection:', error);
    }
    
    setGrades(gradesData);
    setAssignments(assignmentsData);
    setLoading(false);
  };

  // Calculate percentage and letter grade (matching iOS GradingScale)
  const calculateGradeInfo = (grade) => {
    if (!grade || grade.score === undefined || grade.maxScore === undefined || grade.maxScore === 0) {
      return {
        percentage: 0,
        letterGrade: 'N/A',
        status: 'N/A'
      };
    }

    const percentage = (grade.score / grade.maxScore) * 100;
    
    // Use the same grading scale as iOS (GradingScale.defaultScale)
    let letterGrade = 'F';
    if (percentage >= 97) letterGrade = 'A+';
    else if (percentage >= 93) letterGrade = 'A';
    else if (percentage >= 90) letterGrade = 'A-';
    else if (percentage >= 87) letterGrade = 'B+';
    else if (percentage >= 83) letterGrade = 'B';
    else if (percentage >= 80) letterGrade = 'B-';
    else if (percentage >= 77) letterGrade = 'C+';
    else if (percentage >= 73) letterGrade = 'C';
    else if (percentage >= 70) letterGrade = 'C-';
    else if (percentage >= 67) letterGrade = 'D+';
    else if (percentage >= 63) letterGrade = 'D';
    else if (percentage >= 60) letterGrade = 'D-';

    return {
      percentage: Math.round(percentage * 10) / 10, // Round to 1 decimal place
      letterGrade,
      status: grade.status || 'GRADED'
    };
  };

  if (children.length === 0) {
    return (
      <div className="empty-children">
        <div className="empty-icon">📊</div>
        <h3>No Children Linked</h3>
        <p>Add your child using their Student ID to view their grades</p>
      </div>
    );
  }

  return (
    <div className="grades-wrapper">
      <ChildTabSelector children={children} onChildSelect={handleChildSelect} />
      
      {selectedChild && (
        <div className="grades-container">
          {loading ? (
            <div className="loading-container">
              <div className="spinner"></div>
              <p>Loading grades...</p>
            </div>
          ) : !student ? (
            <div className="empty-state">
              <p>Student information not found</p>
            </div>
          ) : (
            <>
              <div className="student-header">
                <h2>{student.fullName}</h2>
                <p>Student ID: {student.studentId}</p>
                <p>Class: {classInfo?.name || 'Not assigned'} ({classInfo?.grade || 'N/A'})</p>
              </div>

              {grades.length > 0 && (
                <div className="grades-summary">
                  <div className="summary-stats">
                    <div className="summary-stat">
                      <div className="summary-label">Overall Average</div>
                      <div className="summary-value average">
                        {(() => {
                          const avg = grades.reduce((sum, g) => sum + (g.score / g.maxScore * 100), 0) / grades.length;
                          return avg.toFixed(1) + '%';
                        })()}
                      </div>
                    </div>
                    <div className="summary-stat">
                      <div className="summary-label">Total Grades</div>
                      <div className="summary-value total">{grades.length}</div>
                    </div>
                  </div>
                  <button 
                    className="distribution-toggle"
                    onClick={() => setShowDistribution(!showDistribution)}
                  >
                    {showDistribution ? 'Hide' : 'Show'} Grade Distribution {showDistribution ? '▲' : '▼'}
                  </button>
                  {showDistribution && (
                    <div className="grade-distribution">
                      <div className="grade-badge a">
                        <span className="badge-label">A</span>
                        <span className="badge-count">{grades.filter(g => (g.score / g.maxScore * 100) >= 90).length}</span>
                      </div>
                      <div className="grade-badge b">
                        <span className="badge-label">B</span>
                        <span className="badge-count">{grades.filter(g => (g.score / g.maxScore * 100) >= 80 && (g.score / g.maxScore * 100) < 90).length}</span>
                      </div>
                      <div className="grade-badge c">
                        <span className="badge-label">C</span>
                        <span className="badge-count">{grades.filter(g => (g.score / g.maxScore * 100) >= 70 && (g.score / g.maxScore * 100) < 80).length}</span>
                      </div>
                      <div className="grade-badge d">
                        <span className="badge-label">D</span>
                        <span className="badge-count">{grades.filter(g => (g.score / g.maxScore * 100) >= 60 && (g.score / g.maxScore * 100) < 70).length}</span>
                      </div>
                      <div className="grade-badge f">
                        <span className="badge-label">F</span>
                        <span className="badge-count">{grades.filter(g => (g.score / g.maxScore * 100) < 60).length}</span>
                      </div>
                    </div>
                  )}
                </div>
              )}

              <div className="grades-content">
                <h3>Grades & Assignments</h3>
                
                {grades.length === 0 ? (
                  <div className="empty-state">
                    <p>No grades available yet</p>
                    <p className="empty-subtitle">Grades will appear here once teachers post them</p>
                  </div>
                ) : (
                  <div className="grades-list">
                    {grades.map((grade, index) => {
                      const assignment = assignments.find(a => a.id === grade.assignmentId);
                      const gradeInfo = calculateGradeInfo(grade);
                      const postedDate = grade.gradedAt?.toDate?.() ? 
                        new Date(grade.gradedAt.toDate()).toLocaleDateString() : 
                        grade.createdAt?.toDate?.() ? 
                        new Date(grade.createdAt.toDate()).toLocaleDateString() : 
                        grade.updatedAt?.toDate?.() ? 
                        new Date(grade.updatedAt.toDate()).toLocaleDateString() : 
                        grade.date ? new Date(grade.date).toLocaleDateString() :
                        null;

                      return (
                        <div key={index} className="grade-card clickable" onClick={() => {
                          setSelectedGrade(grade);
                          setSelectedAssignment(assignment || { id: grade.assignmentId, title: 'Assignment', assignmentType: grade.assignmentType || 'Unknown' });
                        }}>
                          <div className="grade-header">
                            <h4>{assignment?.title || 'Assignment'}</h4>
                            <span className="grade-score">
                              {grade.score}/{grade.maxScore}
                            </span>
                          </div>
                          <div className="grade-details-compact">
                            <span className="grade-tag">{assignment?.assignmentType || grade.assignmentType || 'N/A'}</span>
                            <span className="grade-tag">{gradeInfo.percentage?.toFixed(1) || 'N/A'}%</span>
                            <span className="grade-tag letter">{gradeInfo.letterGrade || 'N/A'}</span>
                            {postedDate && <span className="grade-tag">{postedDate}</span>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}

      {selectedGrade && selectedAssignment && (
        <div className="detail-page">
          <button
            className="detail-back-button"
            onClick={() => {
              setSelectedGrade(null);
              setSelectedAssignment(null);
            }}
          >
            ◄ Back to Grades
          </button>
          <GradeDetailView
            grade={selectedGrade}
            assignment={selectedAssignment}
            student={student}
            onClose={() => {
              setSelectedGrade(null);
              setSelectedAssignment(null);
            }}
          />
        </div>
      )}
    </div>
  );
}

export default ParentGradesView;
